import { Navigate, RouteObject } from 'react-router-dom'

import { PublicClientApplication } from '@azure/msal-browser'

import { ProviderLayout, RootLayout } from './components/layouts'
import {
  FeatureGuard,
  HomeGuard,
  Permissions,
  ProjectGuard,
  TenantGuard,
} from './features/authorization'
import {
  EInvoiceDetailsPage,
  EInvoiceHistoryPage,
  EInvoiceOverviewPage,
} from './features/e-invoice'
import { EInvoiceCockpitPage } from './features/e-invoice/e-invoice-cockpit'
import { DocumentsDialog } from './features/e-invoice/e-invoice-history/components/dialog'
import {
  DeleteSuppliers,
  EditSupplierData,
  Suppliers,
} from './features/selfservice/master-data-management'
import {
  CompanyCodes,
  DeleteCompanyCodes,
  EditCompanyCodeData,
} from './features/selfservice/master-data-management/company-codes'
import {
  DeletePackagingData,
  EditPackagingDataData,
  PackagingData,
} from './features/selfservice/master-data-management/packaging-data/components'
import {
  AddSupplierPage,
  BuyerSettingsPage,
  DeliveryForecastDespatchAdvicePage,
  DeliveryForecastPage,
  DeliveryForecastsHistoryPage,
  DeliveryForecastsPage,
  DespatchAdvicePage,
  InvoicePage,
  MasterDataManagementPage,
  MembersPage,
  NotFoundPage,
  OrderPage,
  OrdersHistoryPage,
  OrdersPage,
  RoleAssignmentsPage,
  SetTenantPage,
  SettingsPage,
  SupplierPage,
  SuppliersPage,
  TestPage,
  TrialPage,
} from './pages'

export function createRoutes(
  publicClient: PublicClientApplication,
): RouteObject {
  return {
    element: <ProviderLayout publicClient={publicClient} />,
    children: [
      {
        path: '/',
        element: <RootLayout />,
        children: [
          {
            index: true,
            element: <HomeGuard />,
          },
          {
            path: 'orders',
            element: (
              <ProjectGuard permissionName={Permissions.Project.Worklist}>
                <FeatureGuard feature="orderProcess">
                  <OrdersPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'orders/order/:id',
            element: (
              <ProjectGuard permissionName={Permissions.Project.Worklist}>
                <FeatureGuard feature="orderProcess">
                  <OrderPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'orders/despatch-advice/:id',
            element: (
              <ProjectGuard permissionName={Permissions.Project.Worklist}>
                <FeatureGuard feature="orderProcess">
                  <DespatchAdvicePage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'orders/invoice/:id',
            element: (
              <ProjectGuard permissionName={Permissions.Project.Worklist}>
                <FeatureGuard feature="orderProcess">
                  <InvoicePage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'orders/history',
            element: (
              <ProjectGuard permissionName={Permissions.Project.History}>
                <FeatureGuard feature="orderProcess">
                  <OrdersHistoryPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'delivery-forecasts',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.DeliveryForecastWorklist}
              >
                <FeatureGuard feature="deliveryForecast">
                  <DeliveryForecastsPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'delivery-forecasts/delivery-forecast/:id',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.DeliveryForecastWorklist}
              >
                <FeatureGuard feature="deliveryForecast">
                  <DeliveryForecastPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'delivery-forecasts/despatch-advice/:id',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.DeliveryForecastWorklist}
              >
                <FeatureGuard feature="deliveryForecast">
                  <DeliveryForecastDespatchAdvicePage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'delivery-forecasts/history',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.DeliveryForecastHistory}
              >
                <FeatureGuard feature="deliveryForecast">
                  <DeliveryForecastsHistoryPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'settings',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.ProjectSettings}
              >
                <SettingsPage />
              </ProjectGuard>
            ),
          },
          {
            path: 'members',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.ProjectUserAdmin}
              >
                <MembersPage />
              </ProjectGuard>
            ),
          },
          {
            path: 'e-invoices',
            element: (
              <ProjectGuard permissionName={Permissions.Project.EInvoice}>
                <FeatureGuard feature="eInvoice">
                  <EInvoiceOverviewPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'e-invoices/errors',
            element: (
              <ProjectGuard permissionName={Permissions.Project.EInvoice}>
                <FeatureGuard feature="eInvoice">
                  <EInvoiceDetailsPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
          },
          {
            path: 'e-invoices/:id',
            element: (
              <ProjectGuard permissionName={Permissions.Project.EInvoice}>
                <FeatureGuard feature="eInvoice">
                  <EInvoiceDetailsPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
            children: [
              {
                path: 'validation',
                element: null,
              },
              {
                path: 'additional-data',
                element: null,
              },
              {
                path: 'additional-documents',
                element: null,
              },
              {
                path: 'verification',
                element: null,
              },
              {
                path: 'summary',
                element: null,
              },
            ],
          },
          {
            path: 'e-invoices/history',
            element: (
              <ProjectGuard
                permissionName={Permissions.Project.EInvoiceHistory}
              >
                <FeatureGuard feature="eInvoice">
                  <EInvoiceHistoryPage />
                </FeatureGuard>
              </ProjectGuard>
            ),
            children: [
              {
                path: 'documents/:id',
                element: <DocumentsDialog />,
              },
            ],
          },
          {
            path: 'demo-functions',
            element: (
              <ProjectGuard permissionName={Permissions.Project.Trial}>
                <TrialPage />
              </ProjectGuard>
            ),
          },
          {
            path: 'test',
            element: <TestPage />,
          },
          {
            path: 'e-invoice-cockpit',
            element: (
              <TenantGuard permissionName={Permissions.Tenant.SupplierCockpit}>
                <EInvoiceCockpitPage />
              </TenantGuard>
            ),
            children: [
              {
                path: 'documents/:id',
                element: <DocumentsDialog />,
              },
            ],
          },
          {
            path: 'suppliers',
            element: (
              <TenantGuard permissionName={Permissions.Tenant.SupplierCockpit}>
                <SuppliersPage />
              </TenantGuard>
            ),
          },
          {
            path: 'suppliers/:id',
            element: (
              <TenantGuard permissionName={Permissions.Tenant.SupplierCockpit}>
                <SupplierPage />
              </TenantGuard>
            ),
          },
          {
            path: 'suppliers/add',
            element: (
              <TenantGuard permissionName={Permissions.Tenant.SupplierCockpit}>
                <AddSupplierPage />
              </TenantGuard>
            ),
          },
          {
            path: 'role-assignments',
            element: (
              <TenantGuard
                permissionName={Permissions.Tenant.CollectionUserAdmin}
              >
                <RoleAssignmentsPage />
              </TenantGuard>
            ),
          },
          {
            path: 'master-data-management',
            element: (
              <TenantGuard permissionName={Permissions.Tenant.MasterData}>
                <MasterDataManagementPage />
              </TenantGuard>
            ),
            children: [
              { index: true, element: <Navigate to="packaging-data" /> },
              {
                path: 'packaging-data',
                element: <PackagingData />,
                children: [
                  {
                    path: 'add',
                    element: <EditPackagingDataData />,
                  },
                  {
                    path: 'edit/:packagingId',
                    element: <EditPackagingDataData />,
                  },
                  {
                    path: 'info/:packagingId',
                    element: <EditPackagingDataData />,
                  },
                  {
                    path: 'delete/:packagingIds',
                    element: <DeletePackagingData />,
                  },
                ],
              },
              {
                path: 'company-codes',
                element: <CompanyCodes />,
                children: [
                  {
                    path: 'add',
                    element: <EditCompanyCodeData />,
                  },
                  {
                    path: 'edit/:companyCodeId',
                    element: <EditCompanyCodeData />,
                  },
                  {
                    path: 'info/:companyCodeId',
                    element: <EditCompanyCodeData />,
                  },
                  {
                    path: 'delete/:companyCodeIds',
                    element: <DeleteCompanyCodes />,
                  },
                ],
              },
              {
                path: 'suppliers',
                element: <Suppliers />,
                children: [
                  {
                    path: 'add',
                    element: <EditSupplierData />,
                  },
                  {
                    path: 'edit/:supplierId',
                    element: <EditSupplierData />,
                  },
                  {
                    path: 'info/:supplierId',
                    element: <EditSupplierData />,
                  },
                  {
                    path: 'delete/:supplierIds',
                    element: <DeleteSuppliers />,
                  },
                ],
              },
            ],
          },
          {
            path: 'buyer-settings',
            element: (
              <TenantGuard
                permissionName={Permissions.Tenant.CollectionSettings}
              >
                <BuyerSettingsPage />
              </TenantGuard>
            ),
          },
          {
            path: 'set-tenant',
            element: <SetTenantPage />,
          },
          { path: '404', element: <NotFoundPage /> },
          { path: '*', element: <Navigate to="/404" /> },
        ],
      },
    ],
  }
}
